import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@auth0/auth0-angular';
import { switchMap, take } from 'rxjs/operators';
import { ApiService, DiligenceQueryParams } from 'src/app/core/services/api.service';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { NavbarComponent } from '../../shared/components/navbar/navbar.component';
import { NavigationRailComponent } from '../../shared/components/navigation-rail/navigation-rail.component';
import { ManualReportAdmin, ManualReportsService } from 'src/app/core/services/manual-reports.service';
import { isReportReady } from 'src/app/core/models/report-readiness';

@Component({
  selector: 'app-admin', standalone: true,
  imports: [CommonModule, FormsModule, NavbarComponent, FooterComponent, NavigationRailComponent],
  templateUrl: './admin.component.html', styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly manualReportsService = inject(ManualReportsService);
  readonly columns = ['Email', 'Type', 'Name', 'Report', 'Created', 'State', 'Priority', ''];
  reports: any[] = [];
  search = '';
  loading = false;
  manualReports: ManualReportAdmin[] = [];
  manualReportsLoading = false;
  manualReportsError = '';
  manualPage = 1;
  manualLimit = 20;
  manualMetadata: any = {};
  selectedManualReport: ManualReportAdmin | null = null;
  manualReportFile: File | null = null;
  manualUploadLoading = false;
  manualUploadMessage = '';
  manualUploadError = '';

  ngOnInit(): void { this.loadReports(); this.loadManualReports(); }
  get filteredReports(): any[] {
    const term = this.search.trim().toLowerCase();
    return term ? this.reports.filter(report => [report.user_email, report.email, report.name, report.target, report.report, report.product, report.uuid].filter(Boolean).join(' ').toLowerCase().includes(term)) : this.reports;
  }
  get totalCases(): number { return this.reports.length; }
  get processingCases(): number { return this.reports.filter(report => ['Pending', 'In review'].includes(this.stateOf(report))).length; }
  get completedCases(): number { return this.reports.filter(report => this.stateOf(report) === 'Complete').length; }
  get escalatedCases(): number { return this.reports.filter(report => this.stateOf(report) === 'Escalated').length; }
  loadReports(): void {
    this.loading = true;
    const query: DiligenceQueryParams = { page: 1, limit: 50, sort: { field: 'updated_at', order: 'desc' }, filters: {} };
    this.auth.user$.pipe(take(1), switchMap(() => this.api.getDiligencesByPhase(query))).subscribe({
      next: response => { this.reports = response.data ?? []; this.loading = false; },
      error: () => { this.reports = []; this.loading = false; }
    });
  }
  clearFilters(): void { this.search = ''; }
  isManualRequest(report: any): boolean {
    return String(report?.report ?? report?.product ?? '').toUpperCase() === 'R10' || report?.manual === true;
  }
  openReport(report: any): void {
    if (this.isManualRequest(report)) {
      const manual = this.manualReports.find(item => item.uuid === report.uuid);
      if (manual) {
        this.selectManualReport(manual);
        document.getElementById('manual-reports-title')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        this.manualUploadError = 'This is a manual request. Select it from the Manual report requests table.';
      }
      return;
    }
    window.open(`/dueDiligence/${report.uuid}`, '_blank');
  }
  loadManualReports(): void {
    this.manualReportsLoading = true;
    this.manualReportsError = '';
    this.manualReportsService.listForAdmin(this.manualPage, this.manualLimit).subscribe({
      next: response => { this.manualReports = response.data ?? []; this.manualMetadata = response.metadata ?? {}; this.manualReportsLoading = false; },
      error: error => { this.manualReports = []; this.manualReportsError = this.manualReportErrorMessage(error); this.manualReportsLoading = false; }
    });
  }
  nextManualPage(): void { if (this.manualMetadata?.totalPages && this.manualPage < this.manualMetadata.totalPages) { this.manualPage++; this.loadManualReports(); } }
  previousManualPage(): void { if (this.manualPage > 1) { this.manualPage--; this.loadManualReports(); } }
  selectManualReport(report: ManualReportAdmin): void {
    this.selectedManualReport = report;
    this.manualReportFile = null;
    this.manualUploadMessage = '';
    this.manualUploadError = '';
  }
  onManualFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.manualReportFile = input.files?.[0] ?? null;
    this.manualUploadMessage = '';
    this.manualUploadError = '';
  }
  uploadManualPdf(): void {
    if (!this.selectedManualReport || !this.manualReportFile) { this.manualUploadError = 'Select a request and a PDF.'; return; }
    this.manualUploadLoading = true; this.manualUploadMessage = ''; this.manualUploadError = '';
    this.manualReportsService.uploadPdf(this.selectedManualReport.uuid, this.manualReportFile).subscribe({
      next: updated => {
        const index = this.manualReports.findIndex(report => report.uuid === updated.uuid);
        if (index >= 0) this.manualReports[index] = updated;
        this.selectedManualReport = updated;
        this.manualReportFile = null;
        this.manualUploadMessage = 'PDF uploaded successfully.';
        this.manualUploadLoading = false;
      },
      error: error => { this.manualUploadError = this.manualReportErrorMessage(error); this.manualUploadLoading = false; }
    });
  }
  private manualReportErrorMessage(error: any): string {
    if (error?.status === 400) return 'Invalid file or non-manual request.';
    if (error?.status === 401) return 'Your session has expired. Please sign in again.';
    if (error?.status === 403) return 'Access denied: administrator permissions are required.';
    if (error?.status === 404) return 'Request or PDF unavailable.';
    if (error?.status === 413) return 'The PDF exceeds the 20 MB limit.';
    if (error?.status === 502) return 'Storage is temporarily unavailable. Please try again.';
    return 'The PDF operation could not be completed. Please try again.';
  }
  stateOf(report: any): string {
    if (isReportReady(report)) return 'Complete';
    if (Number(report?.phase) === 5 || Number(report?.phase) === 6 || String(report?.status ?? '').toUpperCase() === 'COMPLETED') return 'In review';
    return report.status || 'Pending';
  }
  stateClass(report: any): string { return this.stateOf(report).toLowerCase().replace(' ', '-'); }
  targetOf(report: any): string { return report.name || report.target || '—'; }
  emailOf(report: any): string { return report.user_email || report.email || '—'; }
  productOf(report: any): string { return report.report || report.product || '—'; }
}
